import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { X } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import type { AdminAuditRow } from '@repo/shared-types';
import { Badge } from '@repo/ui/src/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/src/components/ui/select';
import { DataTable, type DataTableColumnMeta } from '../components/DataTable';
import { useAdminAudit, useAdminAuditActions } from '../hooks/useAdminAudit';
import { useAdminOrg } from '../hooks/useAdminOrgs';
import { formatDateTime } from '../lib/adminFormat';

const DEFAULT_LIMIT = 50;
const DEFAULT_SORT = 'createdAt:desc';
const ALL_ACTIONS = '__all__';

const columns: ColumnDef<AdminAuditRow, unknown>[] = [
  {
    id: 'createdAt',
    header: 'Thời gian',
    meta: {
      sortField: 'createdAt',
      cellClassName: 'whitespace-nowrap tabular-nums text-surface-600',
    } satisfies DataTableColumnMeta,
    cell: ({ row }) => formatDateTime(row.original.createdAt),
  },
  {
    id: 'organization',
    header: 'Tổ chức',
    // Platform-level actions (a user, not an org) carry no organizationId — linking
    // to /orgs/null would render a blank, dead link rather than an obvious gap.
    cell: ({ row }) => {
      const { organizationId, organizationName } = row.original;
      if (!organizationId) return <span className="text-surface-400">—</span>;
      return (
        <Link to={`/orgs/${organizationId}`} className="hover:underline">
          {organizationName}
        </Link>
      );
    },
  },
  {
    id: 'actor',
    header: 'Người thực hiện',
    cell: ({ row }) => row.original.actorEmail ?? '—',
  },
  {
    id: 'action',
    header: 'Hành động',
    cell: ({ row }) => <Badge variant="secondary">{row.original.action}</Badge>,
  },
  {
    id: 'entityType',
    header: 'Đối tượng',
    cell: ({ row }) => row.original.entityType,
  },
  {
    id: 'metadata',
    header: 'Chi tiết',
    meta: { cellClassName: 'max-w-xs' } satisfies DataTableColumnMeta,
    cell: ({ row }) => <MetadataCell metadata={row.original.metadata} />,
  },
];

function MetadataCell({ metadata }: { metadata: unknown }) {
  const [expanded, setExpanded] = useState(false);
  const text = useMemo(() => JSON.stringify(metadata ?? {}), [metadata]);
  if (text === '{}') return <span className="text-muted-foreground">—</span>;

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="block max-w-xs truncate text-left text-surface-600 hover:underline"
        title="Xem chi tiết"
      >
        {text}
      </button>
    );
  }
  return (
    <div className="max-w-xs overflow-x-auto">
      <pre className="whitespace-pre text-xs text-surface-700">{JSON.stringify(metadata, null, 2)}</pre>
      <button type="button" onClick={() => setExpanded(false)} className="mt-1 text-xs text-surface-500 hover:underline">
        Thu gọn
      </button>
    </div>
  );
}

export function AuditPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page')) || 1;
  const limit = Number(searchParams.get('limit')) || DEFAULT_LIMIT;
  const sort = searchParams.get('sort') || DEFAULT_SORT;
  const orgId = searchParams.get('orgId') ?? undefined;
  const action = searchParams.get('action') ?? undefined;

  const params = useMemo(() => ({ page, limit, sort, orgId, action }), [page, limit, sort, orgId, action]);
  const { data, isLoading, error } = useAdminAudit(params);

  // Distinct actions across the whole table, from a dedicated endpoint — not
  // derived from the currently loaded page, so an operator can filter by an
  // action that hasn't appeared on a page they've viewed yet.
  const { data: knownActions } = useAdminAuditActions();

  const { data: orgDetail } = useAdminOrg(orgId);

  const setPage = (nextPage: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(nextPage));
    setSearchParams(next, { replace: true });
  };

  const setSort = (nextSort: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('sort', nextSort);
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  };

  const setAction = (nextAction: string) => {
    const next = new URLSearchParams(searchParams);
    if (nextAction === ALL_ACTIONS) next.delete('action');
    else next.set('action', nextAction);
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  };

  const clearOrgFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('orgId');
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-surface-900">Audit log</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Nhật ký hoạt động của quản trị viên trên toàn nền tảng.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {orgId && (
          <Badge variant="outline" className="gap-1.5 py-1">
            Tổ chức: {orgDetail?.name ?? orgId}
            <button type="button" onClick={clearOrgFilter} aria-label="Bỏ lọc theo tổ chức">
              <X className="h-3 w-3" />
            </button>
          </Badge>
        )}

        <Select value={action ?? ALL_ACTIONS} onValueChange={setAction}>
          <SelectTrigger className="w-56">
            <SelectValue placeholder="Tất cả hành động" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_ACTIONS}>Tất cả hành động</SelectItem>
            {(knownActions ?? []).map((a) => (
              <SelectItem key={a} value={a}>
                {a}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DataTable
        columns={columns}
        data={data?.rows ?? []}
        total={data?.total ?? 0}
        page={page}
        limit={limit}
        sort={sort}
        onPageChange={setPage}
        onSortChange={setSort}
        isLoading={isLoading}
        error={error}
        emptyText="Không có bản ghi nào."
      />
    </div>
  );
}
